import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { GALLERY, GALLERY_CATS } from "@/lib/clinicImages";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-accent px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-primary">
    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
    {children}
  </span>
);

export default function Gallery() {
  const [active, setActive] = useState("todos");
  const [lightbox, setLightbox] = useState(null);

  const items = active === "todos" ? GALLERY : GALLERY.filter((g) => g.cat === active);

  return (
    <section id="galeria" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-2xl text-center">
        <Eyebrow>Galería</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-semibold text-foreground sm:text-5xl">Conoce nuestra clínica</h2>
        <p className="mt-4 text-muted-foreground">
          Un espacio moderno y acogedor, con la profesional y el equipamiento que cuidan de tus pies.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap justify-center gap-2.5" data-testid="gallery-filters">
        {GALLERY_CATS.map((c) => (
          <button
            key={c.id}
            onClick={() => setActive(c.id)}
            data-testid={`gallery-filter-${c.id}`}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-all ${
              active === c.id
                ? "bg-primary text-primary-foreground shadow-sm"
                : "border border-border bg-white text-foreground/70 hover:border-primary/40 hover:text-primary"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="mt-10 columns-1 gap-5 sm:columns-2 lg:columns-3 [&>*]:mb-5">
        {items.map((it, i) => (
          <motion.button
            key={it.src}
            layout
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
            onClick={() => setLightbox(it)}
            data-testid={`gallery-item-${i}`}
            className="group relative block w-full break-inside-avoid overflow-hidden rounded-2xl shadow-sm ring-1 ring-border transition-all hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <img src={it.src} alt={it.alt} loading="lazy" className="w-full transition-transform duration-500 group-hover:scale-105" />
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2a1233]/55 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <span className="pointer-events-none absolute bottom-4 left-4 right-4 translate-y-2 text-left text-sm font-semibold text-white opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
              {it.alt}
            </span>
          </motion.button>
        ))}
      </div>

      <Dialog open={!!lightbox} onOpenChange={(o) => (!o ? setLightbox(null) : null)}>
        <DialogContent className="max-w-3xl overflow-hidden border-none bg-transparent p-0 shadow-none">
          {lightbox && (
            <figure>
              <img src={lightbox.src} alt={lightbox.alt} className="max-h-[80vh] w-full rounded-2xl object-contain" />
              <figcaption className="mt-3 text-center text-sm font-medium text-white">{lightbox.alt}</figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
